import { Alert, SxProps, Typography } from "@outschool/backpack";
import {
  FundingProgramAbbreviation,
  getFundingProgramProperties,
} from "@outschool/funding-programs-shared";
import { Trans, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useFundingProgramAbbr, useSession } from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import { useSelfPacedIconInfo } from "@outschool/ui-components-website";
import React from "react";

import { useTimeZone } from "../../providers/TimeZoneProvider";

import type { SelfPacedIconInfoActivityType } from "@outschool/ui-components-website";

type UseVirginiaSelfPacedAdjustmentProps = {
  activity: SelfPacedIconInfoActivityType;
  selfPacedStartTime: Date;
};

export function useVirginiaSelfPacedAdjustment({
  activity,
  selfPacedStartTime,
}: UseVirginiaSelfPacedAdjustmentProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\VirginiaLAGAdjustmentAlert",
  );
  const { isEsaSession } = useSession();
  const esaState = useFundingProgramAbbr();
  const { timeZone } = useTimeZone();

  const selfPacedIconInfo = useSelfPacedIconInfo(activity);

  // show select start time screen
  const endDate = dayjs.min(
    dayjs(selfPacedStartTime).add(activity.duration_weeks!, "weeks"),
    dayjs("5/30/2024").tz(timeZone).startOf("date"),
  );
  const daysOfFeedback = Math.ceil(
    dayjs(endDate).diff(
      dayjs(selfPacedStartTime).tz(timeZone).startOf("date"),
      "day",
      true,
    ),
  );
  const hasWeeksOfFeedbackAdjustment =
    !!activity.duration_weeks &&
    isEsaSession &&
    esaState === FundingProgramAbbreviation.VA &&
    daysOfFeedback < activity.duration_weeks * 7;

  const feedbackTimeText = hasWeeksOfFeedbackAdjustment
    ? t("Teacher support until 5/30/24")
    : selfPacedIconInfo.weeksOfFeedback.text;

  const value = React.useMemo(() => {
    return {
      hasWeeksOfFeedbackAdjustment,
      feedbackTimeText,
      endDate: hasWeeksOfFeedbackAdjustment
        ? dayjs.min(
            dayjs(selfPacedStartTime).add(activity.duration_weeks!, "weeks"),
            dayjs("5/30/2024").tz(timeZone).startOf("date"),
          )
        : dayjs(selfPacedStartTime).add(activity.duration_weeks!, "weeks"),
    };
  }, [
    hasWeeksOfFeedbackAdjustment,
    feedbackTimeText,
    timeZone,
    selfPacedStartTime,
    activity.duration_weeks,
  ]);

  return value;
}

export function VirginiaLAGAdjustmentAlert({ alertSx }: { alertSx?: SxProps }) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\VirginiaLAGAdjustmentAlert",
  );
  const fundingProgramUrl = getFundingProgramProperties(
    FundingProgramAbbreviation.VA,
  )?.programUrl!;

  return (
    <Alert severity="warning" sx={alertSx}>
      <Typography variant="body2">
        {/* eslint-disable react/no-unescaped-entities */}
        <Trans t={t}>
          <span>
            Per the terms of{" "}
            <ExternalLink url={fundingProgramUrl}>
              Virginia's K-12 Learning Acceleration Grants
            </ExternalLink>
            , the end date for teacher feedback for this class must be
            5/30/2024. You will still have access to all the class content for a
            year.
          </span>
        </Trans>
        {/* eslint-enable react/no-unescaped-entities */}
      </Typography>
    </Alert>
  );
}
